import { Component, computed, inject, signal, ChangeDetectionStrategy } from "@angular/core";

import {
  EmptyStateComponent,
  IButtonDirective,
  IInputDirective,
  MODAL_DATA,
  ModalRef,
  ModalService,
  IIconComponent,
} from "@front/m19-ui";
import { AdvertisingStrategyEx, SegmentConfigType, StrategyGroupEx } from "@front/m19-models";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { StrategyTactic, TacticType } from "@front/m19-api-client";
import { AccountSelectionService, SegmentService } from "@front/m19-services";
import { switchMap } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { TacticComponent } from "@m19-board/strategies/tactic/tactic.component";
import {
  TacticAddPopupComponent,
  TacticAddPopupModalData,
} from "@m19-board/strategies/strategy-page/tactic-add-popup.component";
import { FormsModule } from "@angular/forms";

export interface StrategyTacticModalData {
  strategy: AdvertisingStrategyEx;
  isReadOnly: boolean;
  tactics: StrategyTactic[];
  tacticType: TacticType;
  strategyGroup: StrategyGroupEx;
}

@Component({
  selector: "app-strategy-tactic-modal",
  imports: [
    TranslocoDirective,
    IButtonDirective,
    TacticComponent,
    EmptyStateComponent,
    IInputDirective,
    FormsModule,
    IIconComponent,
  ],
  templateUrl: "./strategy-tactic-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyTacticModalComponent {
  private readonly segmentService = inject(SegmentService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);

  readonly TacticType = TacticType;

  readonly modalRef = inject(ModalRef);
  readonly modalData = inject<StrategyTacticModalData>(MODAL_DATA);

  tactics = signal<StrategyTactic[]>(this.modalData.tactics);
  filteredTactics = computed(() =>
    this.tactics().filter((t) => this.segmentIndex()?.get(t.segmentId)?.name.includes(this.tacticSearchInput())),
  );

  segmentIndex = toSignal(
    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
    ),
  );

  tacticSearchInput = signal("");

  disableProductSegment = !this.modalData.strategy.isSpStrategy() || this.modalData.strategy.disableProductSegment;

  createTactic(): void {
    const allowedSegmentTacticTypes: [SegmentConfigType, TacticType][] = [];
    if (this.modalData.tacticType == TacticType.LEGACY)
      allowedSegmentTacticTypes.push([SegmentConfigType.KeywordSegment, TacticType.LEGACY]);
    else allowedSegmentTacticTypes.push([SegmentConfigType.KeywordSegment, TacticType.BLACKLIST]);

    if (this.modalData.strategy.isSpStrategy() && this.modalData.strategy.productTargetingEnabled) {
      if (this.modalData.tacticType == TacticType.LEGACY)
        allowedSegmentTacticTypes.push([SegmentConfigType.ProductSegment, TacticType.LEGACY]);
      else allowedSegmentTacticTypes.push([SegmentConfigType.ProductSegment, TacticType.BLACKLIST]);
    }

    const ref = this.modalService.openModal<TacticAddPopupModalData, StrategyTactic>(TacticAddPopupComponent, {
      modalTitle: this.translocoService.translate("strategy-page.tactics"),
      data: { strategy: this.modalData.strategy, allowedSegmentTacticTypes },
    });

    ref.afterClosed().subscribe((tactic) => {
      if (tactic) {
        this.tactics.update((tactics) => [...tactics, tactic]);
      }
    });
  }

  tacticDeleted(tactic: StrategyTactic): void {
    this.tactics.update((tactics) => tactics.filter((t) => t.segmentId !== tactic.segmentId));
  }
}
